/**
 * The FFmpeg concat script, ported from agent/internal/video/video.go.
 *
 * The console runs the agent's own FFmpeg command (see app/lib/video.ts), and
 * this builds the demuxer script it reads. It lives in `core/` because it is
 * parity logic — the timings come from PrintWithLayout.GenerateGif() by way of
 * the Go build — and because being pure makes it the one part of the video path
 * that can be tested without a 32MB WebAssembly download.
 */

/** Half a second per frame, the whole sequence four times. */
export const SECONDS_PER_FRAME = 0.5
export const LOOPS = 4

/**
 * Escapes one entry for the concat demuxer.
 *
 * It treats a backslash as an escape and a quote as a delimiter, so both have to
 * be escaped — the same two replacements the Go version applied to Windows
 * paths. The names written here are generated rather than taken from the camera,
 * so in practice there is nothing to escape; it is kept because the cost of
 * being wrong is a corrupt script and a missing video, and the cost of keeping
 * it is one line.
 */
export function escapeConcatName(name: string): string {
  return name.replace(/\\/g, '\\\\').replace(/'/g, "'\\''")
}

/**
 * Builds the script.
 *
 * The last entry is repeated WITHOUT a duration on purpose: the concat demuxer
 * holds a final entry for no time otherwise, and the last frame flashes past.
 * That trailing line is why the finished video is a touch longer than
 * `loops × frames × 0.5s`.
 */
export function concatScript(names: string[]): string {
  if (names.length === 0) throw new Error('concatScript: no files')

  let script = ''
  for (let loop = 0; loop < LOOPS; loop++) {
    for (const name of names) {
      script += `file '${escapeConcatName(name)}'\nduration ${SECONDS_PER_FRAME.toFixed(1)}\n`
    }
  }
  script += `file '${escapeConcatName(names[names.length - 1]!)}'\n`
  return script
}

/** How long the encoded video runs, in seconds — the trailing hold included. */
export function expectedDuration(frameCount: number): number {
  return (LOOPS * frameCount + 1) * SECONDS_PER_FRAME
}
